import React, { useEffect, useRef, useState, useCallback } from "react";
import jsQR from "jsqr";
import { useAuth } from "../context/AuthContext";
import { apiFetch } from "../api/client";
import { COLORS, RADIUS } from "../theme";
import Card from "../components/ui/Card";
import Btn from "../components/ui/Btn";
import Badge from "../components/ui/Badge";
import Icon from "../components/ui/Icon";
import Aviso from "../components/ui/Aviso";
import EstadoCarregando from "../components/ui/EstadoCarregando";
import EstadoVazio from "../components/ui/EstadoVazio";
import { nfcDisponivel, lerNfcUmaVez } from "../utils/nfc";
import { capturarMelhorLocalizacao } from "../utils/geolocalizacao";

function horaLocal(iso) {
  return new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

function formatarDuracao(segundos) {
  if (segundos == null) return "—";
  const h = Math.floor(segundos / 3600);
  const m = Math.floor((segundos % 3600) / 60);
  const s = segundos % 60;
  if (h > 0) return `${h}h ${m}min`;
  if (m > 0) return `${m}min ${s}s`;
  return `${s}s`;
}

export default function FazerRota() {
  const { token } = useAuth();
  const [rotas, setRotas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [execucao, setExecucao] = useState(null);
  const [iniciando, setIniciando] = useState(null);
  const [erro, setErro] = useState("");

  const [scannerAberto, setScannerAberto] = useState(false);
  const [stream, setStream] = useState(null);
  const [erroScanner, setErroScanner] = useState("");
  const [validando, setValidando] = useState(false);
  const [lendoNfc, setLendoNfc] = useState(false);
  const [feedback, setFeedback] = useState(null); // { tipo: 'sucesso'|'repetido'|'erro', texto }
  const [concluida, setConcluida] = useState(false);

  const videoRef = useRef(null);
  const scanAtivoRef = useRef(false);

  const carregarLista = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiFetch("GET", "/api/rotas-execucoes/minhas-rotas", undefined, token);
      setRotas(data);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { carregarLista(); }, [carregarLista]);

  const iniciarRota = async (rotaId) => {
    setErro("");
    setIniciando(rotaId);
    try {
      const data = await apiFetch("POST", "/api/rotas-execucoes/iniciar", { rota_id: rotaId }, token);
      setExecucao(data);
      setConcluida(data.status === "concluida");
    } catch (e) {
      setErro(e.message);
    } finally {
      setIniciando(null);
    }
  };

  const voltarParaLista = () => {
    setExecucao(null);
    setConcluida(false);
    carregarLista();
  };

  const pararScanner = useCallback(() => {
    scanAtivoRef.current = false;
    setScannerAberto(false);
    setStream((s) => {
      if (s) s.getTracks().forEach((t) => t.stop());
      return null;
    });
  }, []);

  const abrirScanner = async () => {
    setErroScanner("");
    setFeedback(null);
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" }, audio: false });
      setStream(s);
      setScannerAberto(true);
    } catch {
      setErroScanner("Não foi possível acessar a câmera. Verifique a permissão no navegador.");
    }
  };

  // Liga o vídeo e roda o loop de leitura de QR frame a frame — mesmo
  // esqueleto de ciclo de vida de câmera usado em BaterPonto.js (garante
  // desligar o stream de verdade ao sair, não só esconder o <video>).
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !stream) return;
    video.srcObject = stream;
    const playPromise = video.play();
    if (playPromise?.catch) playPromise.catch(() => {});

    scanAtivoRef.current = true;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d", { willReadFrequently: true });

    const tick = () => {
      if (!scanAtivoRef.current) return;
      if (video.readyState === video.HAVE_ENOUGH_DATA) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const imagem = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const codigo = jsQR(imagem.data, imagem.width, imagem.height);
        if (codigo?.data) {
          scanAtivoRef.current = false;
          validarCodigo(codigo.data, "qr");
          return;
        }
      }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);

    return () => {
      scanAtivoRef.current = false;
      video.pause();
      video.srcObject = null;
      stream.getTracks().forEach((t) => t.stop());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stream]);

  const validarCodigo = async (codigo, metodo) => {
    setValidando(true);
    pararScanner();
    let coords = {};
    try {
      const local = await capturarMelhorLocalizacao({ duracaoMs: 3000, precisaoAlvo: 30 });
      if (local) coords = { latitude: local.latitude, longitude: local.longitude };
    } catch {
      // localização é só um extra aqui — o que garante presença é o NFC/QR, não o GPS
    }

    try {
      const resultado = await apiFetch("POST", `/api/rotas-execucoes/${execucao.id}/validar`, { codigo, metodo, ...coords }, token);
      if (resultado.repetido) {
        setFeedback({ tipo: "repetido", texto: resultado.mensagem });
      } else {
        setFeedback({
          tipo: "sucesso",
          texto: resultado.finalizada
            ? `"${resultado.ponto_nome}" confirmado! Rota concluída — bom trabalho.`
            : `"${resultado.ponto_nome}" confirmado! Pode ir pro próximo ponto.`,
        });
        if (resultado.finalizada) setConcluida(true);
      }
      const atualizado = await apiFetch("GET", `/api/rotas-execucoes/${execucao.id}`, undefined, token);
      setExecucao(atualizado);
    } catch (e) {
      setFeedback({ tipo: "erro", texto: e.message });
    } finally {
      setValidando(false);
    }
  };

  const lerNfc = async () => {
    setFeedback(null);
    setLendoNfc(true);
    try {
      const codigo = await lerNfcUmaVez();
      await validarCodigo(codigo, "nfc");
    } catch (e) {
      setFeedback({ tipo: "erro", texto: e.message });
    } finally {
      setLendoNfc(false);
    }
  };

  const finalizarManual = async () => {
    if (!window.confirm("Finalizar essa rota mesmo com pontos pendentes?")) return;
    await apiFetch("POST", `/api/rotas-execucoes/${execucao.id}/finalizar`, undefined, token);
    const atualizado = await apiFetch("GET", `/api/rotas-execucoes/${execucao.id}`, undefined, token);
    setExecucao(atualizado);
    setConcluida(true);
  };

  if (loading) return <EstadoCarregando />;

  // ── Tela de execução ativa (ou concluída) ──
  if (execucao) {
    const totalPontos = execucao.pontos.length;
    const totalValidados = execucao.pontos.filter((p) => p.validado).length;

    return (
      <div>
        <button onClick={voltarParaLista} style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", color: COLORS.textMid, fontSize: 13, fontWeight: 600, cursor: "pointer", padding: 0, marginBottom: 16 }}>
          <Icon name="arrowLeft" size={15} /> Minhas rotas
        </button>

        <Card style={{ marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
            <h1 style={{ fontSize: 19, fontWeight: 800, color: COLORS.text, margin: 0 }}>{execucao.rota_nome}</h1>
            <Badge tone={concluida ? "success" : "primary"}>{concluida ? "Concluída" : "Em andamento"}</Badge>
          </div>
          <p style={{ fontSize: 13, color: COLORS.textMid, margin: "4px 0 0" }}>
            {totalValidados} de {totalPontos} pontos confirmados
            {concluida && execucao.duracao_total_segundos != null && ` · Duração total: ${formatarDuracao(execucao.duracao_total_segundos)}`}
          </p>
        </Card>

        {feedback && (
          <Aviso
            tone={feedback.tipo === "sucesso" ? "success" : feedback.tipo === "repetido" ? "warning" : "danger"}
            icon={feedback.tipo === "sucesso" ? "checkCircle" : feedback.tipo === "repetido" ? "alertCircle" : "alertCircle"}
            onClose={() => setFeedback(null)}
          >
            {feedback.texto}
          </Aviso>
        )}

        {!concluida && (
          <Card style={{ marginBottom: 16 }}>
            {scannerAberto ? (
              <div>
                <div style={{ position: "relative", borderRadius: RADIUS.md, overflow: "hidden", background: "#000", aspectRatio: "1" }}>
                  <video ref={videoRef} muted playsInline style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  <div style={{ position: "absolute", inset: "15%", border: "3px solid rgba(255,255,255,0.7)", borderRadius: 16, pointerEvents: "none" }} />
                </div>
                <p style={{ fontSize: 12.5, color: COLORS.textMid, textAlign: "center", margin: "10px 0" }}>
                  Aponte a câmera pro QR code do ponto.
                </p>
                <Btn variant="secondary" onClick={pararScanner} style={{ width: "100%" }}>Cancelar</Btn>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {erroScanner && (
                  <div style={{ color: COLORS.danger, fontSize: 13, background: COLORS.dangerBg, border: "1px solid #FECACA", borderRadius: 8, padding: "9px 13px" }}>
                    {erroScanner}
                  </div>
                )}
                <Btn onClick={abrirScanner} disabled={validando} style={{ width: "100%" }}>
                  <Icon name="qrCode" size={17} /> Escanear QR code
                </Btn>
                {nfcDisponivel() && (
                  <Btn variant="secondary" onClick={lerNfc} disabled={validando || lendoNfc} style={{ width: "100%" }}>
                    <Icon name="nfc" size={17} /> {lendoNfc ? "Encoste o celular no cartão..." : "Ler cartão NFC"}
                  </Btn>
                )}
                <button onClick={finalizarManual} style={{ background: "none", border: "none", color: COLORS.textLight, fontSize: 12.5, cursor: "pointer", padding: 4 }}>
                  Finalizar rota mesmo com pontos pendentes
                </button>
              </div>
            )}
          </Card>
        )}

        <Card style={{ padding: 0 }}>
          {execucao.pontos.map((p) => (
            <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 18px", borderBottom: `1px solid ${COLORS.border}` }}>
              <div style={{
                width: 30, height: 30, borderRadius: "50%", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
                background: p.validado ? COLORS.successBg : COLORS.bg, color: p.validado ? COLORS.success : COLORS.textLight,
              }}>
                {p.validado ? <Icon name="checkCircle" size={16} /> : <span style={{ fontSize: 12, fontWeight: 700 }}>{p.ordem}</span>}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: COLORS.text }}>{p.nome}</div>
                {p.endereco && <div style={{ fontSize: 11.5, color: COLORS.textLight }}>{p.endereco}</div>}
              </div>
              {p.validado && (
                <div style={{ textAlign: "right", fontSize: 11.5, color: COLORS.textMid }}>
                  <div>{horaLocal(p.validado_em)}</div>
                  {p.segundos_desde_anterior != null && (
                    <div style={{ color: COLORS.textLight }}>+{formatarDuracao(p.segundos_desde_anterior)}</div>
                  )}
                </div>
              )}
            </div>
          ))}
        </Card>
      </div>
    );
  }

  // ── Lista de rotas escaladas ──
  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 800, color: COLORS.text, margin: "0 0 4px", letterSpacing: "-0.02em" }}>
          Minhas Rotas
        </h1>
        <p style={{ fontSize: 13.5, color: COLORS.textMid, margin: 0 }}>
          Cronogramas de limpeza, ronda ou vistoria escalados pra você.
        </p>
      </div>

      {erro && (
        <div style={{ color: COLORS.danger, fontSize: 13, background: COLORS.dangerBg, border: "1px solid #FECACA", borderRadius: 8, padding: "9px 13px", marginBottom: 16 }}>
          {erro}
        </div>
      )}

      {rotas.length === 0 ? (
        <Card>
          <EstadoVazio icon="route" texto="Você ainda não está escalado em nenhuma rota." />
        </Card>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {rotas.map((r) => (
            <Card key={r.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.text }}>{r.nome}</div>
                <div style={{ fontSize: 12.5, color: COLORS.textMid, marginTop: 2 }}>
                  {r.total_pontos} {r.total_pontos === 1 ? "ponto" : "pontos"} de verificação
                  {r.execucao_em_andamento && " · em andamento"}
                </div>
              </div>
              <Btn onClick={() => iniciarRota(r.id)} disabled={iniciando === r.id}>
                {iniciando === r.id ? "Abrindo..." : r.execucao_em_andamento ? "Continuar" : "Iniciar"}
              </Btn>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
